/**
 * A heuristic computer opponent. `nextAction` looks at the position and returns the side's next
 * legal action; whoever hosts the game applies it (dealing chance as for any player, R-RNG-03)
 * and asks again until the turn has passed. Each call reads the position afresh: the bot keeps
 * no memory and the same position always gets the same answer.
 *
 * A turn, in order: badly damaged ships in an own port go into dock (R-SREP-01); the squadron
 * is declared and the wind cast (R-TURN-03/04); each ship sails to the best cell it can reach;
 * shooters fire, a volley when it can sink the target (R-VOLLEY-03), single shots otherwise;
 * the turn ends. A cell is worth what the ship can hit from it, less the fire it can expect
 * back and the chance of a storm driving it onto a reef, less the way still to go: to the
 * enemy fleet, or to the enemy Royal Port for the raiders (the ships nearest it, R-ROYAL-04),
 * home for a ship that needs repair, back to the own Royal Port while the enemy closes in.
 */
import {
  applyAction,
  declarableShips,
  DIRECTION_VECTORS,
  fireTargets,
  indexMap,
  otherSide,
  parseCellId,
  rayBetween,
  reachableCells,
  type Action,
  type CellId,
  type FortState,
  type GameState,
  type MapIndex,
  type Point,
  type ShipState,
  type Side,
} from '@brig/rules-engine';

/** The side's next action. Only the side to move asks, while the game is on. */
export function nextAction(state: GameState, side: Side): Action {
  if (state.status.kind !== 'playing' || state.turn.side !== side) {
    throw new Error(`It is not ${side}'s turn`);
  }
  const look = new Look(state, side);
  return (
    dock(look) ??
    declare(look) ??
    (state.turn.phase === 'declared' ? { type: 'spinForMovement', side } : null) ??
    sail(look) ??
    pickFire(state, side) ?? { type: 'endTurn', side }
  );
}

const legal = (state: GameState, action: Action): boolean => applyAction(state, action).ok;

function at(cell: CellId): Point {
  const p = parseCellId(cell);
  if (!p) throw new Error(`Bad cell ${cell}`);
  return p;
}

const apart = (a: Point, b: Point) => Math.max(Math.abs(a.x - b.x), Math.abs(a.y - b.y));

/** Weights of a cell's worth; see the module comment. */
const W = {
  attack: 3,
  risk: 6,
  approach: 0.6,
  raid: 1,
  defend: 1.2,
  home: 2,
  inPort: 4,
  holdRoyal: 25,
  /** A ship lost on a reef, times the chance of the storm that drives it there. */
  wreck: 20,
};

/** Ships sent to the enemy Royal Port: one in four, at least one. */
const RAIDERS = 0.25;

/** How far an enemy ship may come before it fires, beyond its range, for the risk of a cell. */
const CLOSING = 3;

/** What the bot knows of the position, worked out once per call. */
class Look {
  readonly index: MapIndex;
  readonly enemy: Side;
  readonly ownShips: readonly ShipState[];
  readonly enemyShips: readonly ShipState[];
  readonly enemyForts: readonly FortState[];
  readonly ownRoyal: Point | null;
  readonly enemyRoyal: Point | null;
  /** An enemy ship is near the own Royal Port: everyone turns back to defend it. */
  readonly royalThreatened: boolean;
  private readonly fields = new Map<string, Float64Array>();
  private raiderIds: ReadonlySet<string> | null = null;
  readonly state: GameState;
  readonly side: Side;

  constructor(state: GameState, side: Side) {
    this.state = state;
    this.side = side;
    this.index = indexMap(state.map);
    this.enemy = otherSide(side);
    this.ownShips = state.ships.filter((s) => s.side === side && !s.sunk);
    this.enemyShips = state.ships.filter((s) => s.side === this.enemy && !s.sunk);
    this.enemyForts = state.forts.filter((f) => f.side === this.enemy && !f.destroyed);
    this.ownRoyal = this.index.royalCell(side);
    this.enemyRoyal = this.index.royalCell(this.enemy);
    const own = this.ownRoyal;
    this.royalThreatened = own !== null && this.enemyShips.some((s) => apart(at(s.cell), own) <= 4);
  }

  /** Steps over water from `p` to the nearest of the named places, as this side may sail. */
  distance(to: 'enemy' | 'enemyRoyal' | 'ownRoyal' | 'home', p: Point): number {
    let field = this.fields.get(to);
    if (!field) {
      field = this.flood(this.sources(to));
      this.fields.set(to, field);
    }
    return field[p.y * this.index.width + p.x] ?? Infinity;
  }

  private sources(to: 'enemy' | 'enemyRoyal' | 'ownRoyal' | 'home'): Point[] {
    switch (to) {
      case 'enemy':
        return this.enemyShips.map((s) => at(s.cell));
      case 'enemyRoyal':
        return this.enemyRoyal ? [this.enemyRoyal] : [];
      case 'ownRoyal':
        return this.ownRoyal ? [this.ownRoyal] : [];
      case 'home':
        return this.state.forts
          .filter((f) => f.side === this.side && !f.destroyed)
          .flatMap((f) => f.portCells.map(at));
    }
  }

  /** Breadth-first over the cells this side's ships may enter (R-MOVE-05/06/09/10). */
  private flood(sources: readonly Point[]): Float64Array {
    const { width, height } = this.index;
    const field = new Float64Array(width * height).fill(Infinity);
    const queue: Point[] = [];
    for (const p of sources) {
      field[p.y * width + p.x] = 0;
      queue.push(p);
    }
    for (let i = 0; i < queue.length; i++) {
      const p = queue[i] as Point;
      const next = (field[p.y * width + p.x] ?? 0) + 1;
      for (const v of Object.values(DIRECTION_VECTORS)) {
        const q = { x: p.x + v.x, y: p.y + v.y };
        if (!this.enterable(q)) continue;
        const k = q.y * width + q.x;
        if ((field[k] ?? 0) <= next) continue;
        field[k] = next;
        queue.push(q);
      }
    }
    return field;
  }

  private enterable(p: Point): boolean {
    const { index } = this;
    if (!index.inBounds(p) || index.isFortCell(p)) return false;
    const type = index.type(p);
    if (type === 'land' || type === 'reef' || index.royalOf(p) === this.side) return false;
    const port = index.portOf(p);
    return port === null || !this.enemyForts.some((f) => f.id === port);
  }

  /** R-FIRE-01/02: `to` lies on a ray from `from` within `range`, no land or fort between. */
  inLine(from: Point, to: Point, range: number): boolean {
    const ray = rayBetween(from, to);
    if (!ray || ray.distance > range) return false;
    if (ray.direction === null || !this.state.ruleset.landBlocksFire) return true;
    const v = DIRECTION_VECTORS[ray.direction];
    for (let i = 1; i < ray.distance; i++) {
      const p = { x: from.x + v.x * i, y: from.y + v.y * i };
      if (this.index.type(p) === 'land' || this.index.isFortCell(p)) return false;
    }
    return true;
  }

  /** Hits a ship standing at `p` can expect in the enemy's next turn. */
  threat(p: Point): number {
    const { ruleset } = this.state;
    let shots = 0;
    for (const fort of this.enemyForts) {
      const k = ruleset.fortClasses[fort.cls];
      if (this.inLine(at(fort.cell), p, k.range)) shots += k.shotsPerTurn;
    }
    for (const ship of this.enemyShips) {
      if (ship.dockedOnTurn !== null || ship.repairingFort !== null) continue;
      const k = ruleset.shipClasses[ship.cls];
      const d = apart(at(ship.cell), p);
      if (d <= k.range) shots += k.shotsPerTurn;
      else if (d <= k.range + CLOSING) shots += k.shotsPerTurn / 2;
    }
    return shots * ruleset.hitChance;
  }

  /** The healthy ships nearest the enemy Royal Port, sent to take it (R-ROYAL-04). */
  isRaider(ship: ShipState): boolean {
    if (!this.raiderIds) {
      const healthy = this.ownShips.filter((s) => !needsRepair(this.state, s));
      const n = Math.max(1, Math.round(healthy.length * RAIDERS));
      const near = healthy
        .map((s) => ({ id: s.id, d: this.distance('enemyRoyal', at(s.cell)) }))
        .filter((s) => Number.isFinite(s.d))
        .sort((a, b) => a.d - b.d || a.id.localeCompare(b.id));
      this.raiderIds = new Set(near.slice(0, n).map((s) => s.id));
    }
    return this.raiderIds.has(ship.id);
  }

  /** Of the 24 winds (R-SPIN-01), the storms that would drive a ship from `p` onto a reef. */
  wreckChance(p: Point, drift: number): number {
    const { index } = this;
    let storms = 0;
    for (const v of Object.values(DIRECTION_VECTORS)) {
      for (let i = 1; i <= drift; i++) {
        const q = { x: p.x + v.x * i, y: p.y + v.y * i };
        if (!index.inBounds(q) || index.isFortCell(q) || index.type(q) === 'land') break;
        if (index.type(q) === 'reef') {
          storms++;
          break;
        }
      }
    }
    return storms / 24;
  }

  isOwnPort(p: Point): boolean {
    const port = this.index.portOf(p);
    return this.state.forts.some((f) => f.id === port && f.side === this.side && !f.destroyed);
  }
}

/** Damage that will sink the ship soon, or any damage when no enemy is near: go and mend it. */
function needsRepair(state: GameState, ship: ShipState): boolean {
  const sinksAt = state.ruleset.shipClasses[ship.cls].sinksAt;
  return ship.damage > 0 && (ship.damage * 2 >= sinksAt || sinksAt - ship.damage === 1);
}

/** R-SREP-01: a damaged ship in an own port goes into dock, if it is worth the turns lost. */
function dock(look: Look): Action | null {
  const { state, side } = look;
  for (const ship of look.ownShips) {
    if (ship.damage === 0 || ship.dockedOnTurn !== null || ship.fired) continue;
    const calm = look.enemyShips.every((e) => apart(at(e.cell), at(ship.cell)) > 8);
    if (!needsRepair(state, ship) && !calm) continue;
    const action: Action = { type: 'enterPortRepair', side, shipId: ship.id };
    if (legal(state, action)) return action;
  }
  return null;
}

/** R-TURN-03: every ship that may sail, but not one holding the enemy Royal Port. */
function declare(look: Look): Action | null {
  const { state, side, index } = look;
  if (state.turn.phase !== 'open') return null;
  const shipIds = declarableShips(state).filter((id) => {
    const ship = look.ownShips.find((s) => s.id === id);
    return ship !== undefined && index.royalOf(at(ship.cell)) !== look.enemy;
  });
  if (shipIds.length === 0) return null;
  const action: Action = { type: 'declareMoveGroup', side, shipIds };
  return legal(state, action) ? action : null;
}

/** R-MOVE-*: the first squadron ship with a better cell than its own sails there. */
function sail(look: Look): Action | null {
  const { state, side } = look;
  if (state.turn.phase !== 'spun') return null;
  for (const ship of look.ownShips) {
    if (!ship.inGroup || ship.moved) continue;
    const cells = reachableCells(state, ship.id);
    let best = ship.cell;
    let bestScore = worth(look, ship, ship.cell);
    for (const cell of cells) {
      const score = worth(look, ship, cell);
      if (score > bestScore) {
        best = cell;
        bestScore = score;
      }
    }
    if (best === ship.cell) continue;
    const action: Action = { type: 'moveShip', side, shipId: ship.id, to: best };
    if (legal(state, action)) return action;
  }
  return null;
}

/** What standing at `cell` is worth to `ship` (see the module comment). */
function worth(look: Look, ship: ShipState, cell: CellId): number {
  const { state } = look;
  const k = state.ruleset.shipClasses[ship.cls];
  const p = at(cell);
  const risk = look.threat(p) / (k.sinksAt - ship.damage);
  const danger = W.risk * risk + W.wreck * look.wreckChance(p, k.stormDrift);
  if (needsRepair(state, ship)) {
    return -W.home * look.distance('home', p) - danger + (look.isOwnPort(p) ? W.inPort : 0);
  }
  let score = -danger;
  if (look.index.royalOf(p) === look.enemy) score += W.holdRoyal;
  else if (ship.shotsLeft > 0) score += W.attack * attack(look, p, k.range);
  const raid = look.distance('enemyRoyal', p);
  if (look.isRaider(ship) && Number.isFinite(raid)) score -= W.raid * raid;
  else {
    const way = look.enemyShips.length > 0 ? look.distance('enemy', p) : raid;
    if (Number.isFinite(way)) score -= W.approach * way;
  }
  if (look.royalThreatened) {
    const back = look.distance('ownRoyal', p);
    if (Number.isFinite(back)) score -= W.defend * back;
  }
  return score;
}

/** The enemy a ship at `p` could fire at, weighted: damaged ships and raiders on our Royal Port first. */
function attack(look: Look, p: Point, range: number): number {
  const { state } = look;
  let value = 0;
  for (const enemy of look.enemyShips) {
    const q = at(enemy.cell);
    if (!look.inLine(p, q, range)) continue;
    const sinksAt = state.ruleset.shipClasses[enemy.cls].sinksAt;
    value += 1 + (2 * enemy.damage) / sinksAt + (look.index.royalOf(q) === look.side ? 5 : 0);
  }
  for (const fort of look.enemyForts) {
    if (look.inLine(p, at(fort.cell), range)) value += 0.5;
  }
  return value;
}

/**
 * R-FIRE-*, R-VOLLEY-*: the best target any shooter can reach, then a volley that can sink it
 * in one spin, or else a single shot from the shooter with the fewest other targets.
 */
export function pickFire(state: GameState, side: Side): Action | null {
  const index = indexMap(state.map);
  const shotsOf = new Map<string, number>();
  const reach = new Map<string, string[]>();
  for (const unit of [...state.ships, ...state.forts]) {
    if (unit.side !== side || unit.shotsLeft === 0) continue;
    const targets = fireTargets(state, unit.id);
    if (targets.length === 0) continue;
    shotsOf.set(unit.id, unit.shotsLeft);
    for (const target of targets) reach.set(target, [...(reach.get(target) ?? []), unit.id]);
  }
  let best: { id: string; left: number; shooters: string[]; priority: number } | null = null;
  for (const [id, shooters] of reach) {
    const ship = state.ships.find((s) => s.id === id);
    const fort = ship ? undefined : state.forts.find((f) => f.id === id);
    const max = ship
      ? state.ruleset.shipClasses[ship.cls].sinksAt
      : fort
        ? state.ruleset.fortClasses[fort.cls].destroyedAt
        : 0;
    const left = max - (ship?.damage ?? fort?.hits ?? 0);
    if (left <= 0) continue;
    const total = shooters.reduce((n, s) => n + (shotsOf.get(s) ?? 0), 0);
    const raider = ship !== undefined && index.royalOf(at(ship.cell)) === side;
    const priority = (ship ? 10 : 0) + (raider ? 50 : 0) + 6 * (1 - left / max) + (total >= left ? 4 : 0);
    if (!best || priority > best.priority) best = { id, left, shooters, priority };
  }
  if (!best) return null;

  const { id: targetId, left, shooters } = best;
  if (left >= 2 && shooters.length >= state.ruleset.volleyMinShooters) {
    const volley: { id: string; shots: number }[] = [];
    let committed = 0;
    for (const id of [...shooters].sort((a, b) => (shotsOf.get(b) ?? 0) - (shotsOf.get(a) ?? 0))) {
      if (committed >= left && volley.length >= state.ruleset.volleyMinShooters) break;
      const shots = Math.max(1, Math.min(shotsOf.get(id) ?? 0, left - committed));
      volley.push({ id, shots });
      committed += shots;
    }
    if (committed >= left) {
      const action: Action = { type: 'volley', side, shooters: volley, targetId };
      if (legal(state, action)) return action;
    }
  }
  const shooterId = [...shooters].sort(
    (a, b) => fireTargets(state, a).length - fireTargets(state, b).length,
  )[0];
  if (shooterId === undefined) return null;
  const action: Action = { type: 'fire', side, shooterId, targetId };
  return legal(state, action) ? action : null;
}
